import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import axios from "axios"; 
import { API_URL } from "../Config/api";
import { FAST_API_URL } from "../Config/fast_api";
import { SOCKET_URL } from "../Config/ws";
import { SOCKET_URL_CALL } from "../Config/GrandStreamApi/fast_api";

const CallContext = createContext({});
export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const checkAuth = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token && isModalOpen) {
        setIsModalOpen(false);
        setIncomingCall(null);
        if (socketRef.current) socketRef.current.close();
      }
    }, 2000);

    return () => clearInterval(checkAuth);
  }, [isModalOpen]);

  const socketRef = useRef(null);
  const heartbeatInterval = useRef(null);
  const reconnectTimeout = useRef(null);

  // --- API CALL FUNCTION ---

  const disconnectSocket = useCallback(() => {
    console.log("🛑 Manually Disconnecting Socket...");

    // 1. Clear any pending reconnection timers
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    // 2. Close the socket
    if (socketRef.current) {
      // socketRef.current.onclose
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }

    // 3. Clear Heartbeat
    clearInterval(heartbeatInterval.current);

    // 4. Reset States
    setIncomingCall(null);
    setIsModalOpen(false);
  }, []);

  // CallContext.js 
  const handleCreateCallApi = async (callData, statusOverride = null) => {
    try {
      const savedExt = localStorage.getItem("selected_extension");
      const token = localStorage.getItem("token");
     const userId = localStorage.getItem("clg_id");
    
      const finalStatus = statusOverride || callData.status;

     const payload = {
  uniqueid: callData.uniqueid,
  linkedid: callData.linkedid || callData.uniqueid, 
  src: callData.src,
  dst: callData.dst,
  status: finalStatus, 
  extension: savedExt,
  clg_ref_id: userId ? parseInt(userId, 10) : null, 
};

      console.log(" Hitting API with status:", finalStatus);

      await axios.post(`${SOCKET_URL_CALL}/create-call`, payload, {
        headers: { "Content-Type": "application/json" },
      });

     
      setIncomingCall((prev) => ({ ...prev, status: finalStatus }));
    } catch (error) {
      console.error(" Error hitting Create Call API:", error);
    }
  }; 

  const connectSocket = useCallback((extension) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN)
      return;
    if (!extension) return;

    const wsUrl = `${SOCKET_URL_CALL}/fbackend/ws/calls?exten=${extension}`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      console.log("✅ WebSocket Connected for Ext:", extension);
      heartbeatInterval.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    socket.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        const savedExt = localStorage.getItem("selected_extension");

        if (response.event === "ringing") {
          const { src, dst } = response.data;

         if (src === dst) {
           console.log(
             "🚫 Internal Extension Check detected (src === dst). Modal suppressed.",
           );

         
          if (savedExt && src === savedExt) {
            showSnackbar(
              `Extension ${savedExt} is logged in. Please be ready to pick up calls.`,
              "success",
            );
          }
           return; 
         }

          setIncomingCall({ ...response.data, status: "RINGING" });
          setTimeout(() => {
            // Double check: Call is still ringing after 3 seconds
            setIncomingCall((currentCall) => {
              if (currentCall && currentCall.status === "RINGING") {
                setIsModalOpen(true);
              }
              return currentCall;
            });
          }, 500); // 500ms = 0.5 second delay
        }

        // --- ANSWERED STATE: Hitting API here ---
        else if (response.event === "answered") {
          const answeredData = { ...response.data, status: "ANSWERED" };
          setIncomingCall((prev) => ({
            ...prev,
            ...answeredData,
          }));

          // API Trigger
          handleCreateCallApi(answeredData);
        } else if (response.event === "hangup") {
          setIncomingCall((prev) => ({
            ...prev,
            ...response.data,
            status: "COMPLETED",
          }));

          setTimeout(() => {
            setIsModalOpen(false);
            setIncomingCall(null);
          }, 4000);
        }
      } catch (err) {
        console.error("Socket parse error:", err);
      }
    };

    socket.onclose = () => {
      console.log("🔌 Socket Closed");
      socketRef.current = null;
      clearInterval(heartbeatInterval.current);

      const token = localStorage.getItem("token");
      const extension = localStorage.getItem("selected_extension");

      // ---  CHECK: Reconnect  ---
      if (token && extension) {
        console.log("🔄 Reconnecting in 5s...");
        reconnectTimeout.current = setTimeout(() => {
          connectSocket(extension);
        }, 5000);
      } else {
        console.log("🛑 User logged out, stopping reconnection.");
        setIsModalOpen(false);
        setIncomingCall(null);
      }
    };

    socket.onerror = (err) => {
      console.error("Socket Error:", err);
      socket.close();
    };
  }, []);

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("token");
      const extension = localStorage.getItem("selected_extension");

      if (token && extension) {
        // User logged
        if (
          !socketRef.current ||
          socketRef.current.readyState !== WebSocket.OPEN
        ) {
          connectSocket(extension);
        }
      } else {
        // User logged
        disconnectSocket();
      }
    };

    // Initial check
    checkAuthStatus();

    const authInterval = setInterval(checkAuthStatus, 2000);

    return () => {
      clearInterval(authInterval);
      disconnectSocket();
    };
  }, [connectSocket, disconnectSocket]);

  return (
    <CallContext.Provider
      value={{
        incomingCall,
        isModalOpen,
        connectSocket,
        setIsModalOpen,
        isMinimized, // 2. ADD THIS
        setIsMinimized,
        snackbar,
        setSnackbar,
        showSnackbar,
        disconnectSocket,
        handleCreateCallApi,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};;